import { useState, useEffect, useMemo, useCallback } from "react";
import useSWR from "swr";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Star,
  MagnifyingGlass,
  X,
  CaretUp,
  CaretDown,
  CaretUpDown,
  Terminal,
  ArrowSquareOut,
  ChartLine,
  Copy,
  MapPin,
} from "@phosphor-icons/react";
import { fetchHosts, fetchMeta, toggleFavorite } from "@/lib/api";
import { INV } from "@/constants/testIds";

const COLUMNS = [
  { key: "hostname", label: "Hostname", width: "w-[220px]" },
  { key: "ip", label: "IP Address", width: "w-[140px]" },
  { key: "host_type", label: "Type", width: "w-[120px]" },
  { key: "location_name", label: "Location", width: "w-[240px]" },
  { key: "system_name", label: "System", width: "w-[180px]" },
  { key: "system_tier", label: "Tier", width: "w-[80px]" },
  { key: "environment", label: "Env", width: "w-[90px]" },
  { key: "os", label: "OS", width: "w-[100px]" },
];

const tierBadge = (tier) =>
  tier === "Gold"
    ? "bg-[#FEF3C7] text-[#92400E] border-[#F59E0B]"
    : "bg-[#F3F4F6] text-[#374151] border-[#9CA3AF]";

const envBadge = (env) => {
  switch (env) {
    case "Prod":
      return "bg-[#FEE2E2] text-[#991B1B] border-[#F87171]";
    case "Dev":
      return "bg-[#E0F2FE] text-[#075985] border-[#38BDF8]";
    case "Sandbox":
      return "bg-[#FEF9C3] text-[#854D0E] border-[#FACC15]";
    case "Test":
      return "bg-[#F3E8FF] text-[#6B21A8] border-[#C084FC]";
    default:
      return "bg-[#F3F4F6] text-[#374151] border-[#9CA3AF]";
  }
};

const SNOW_BASE = "https://your-instance.service-now.com/nav_to.do";
const DATADOG_BASE = "https://app.datadoghq.com/infrastructure";
const DASH = "—";

export function HostInventory({ onOpenHost }) {
  const [search, setSearch] = useState("");
  const [favoritesOnly, setFavoritesOnly] = useState(true);
  const [filters, setFilters] = useState({
    host_type: "all",
    system_tier: "all",
    environment: "all",
    os: "all",
  });
  const [sort, setSort] = useState({ key: "hostname", dir: "asc" });
  const [connectHost, setConnectHost] = useState(null);

  const { data: meta } = useSWR("meta", fetchMeta);
  const { data: rawHosts, mutate, isLoading } = useSWR("hosts", () =>
    fetchHosts()
  );

  useEffect(() => {
    if (rawHosts && favoritesOnly && !rawHosts.some((h) => h.is_favorite)) {
      setFavoritesOnly(false);
    }
  }, [rawHosts, favoritesOnly]);

  const filteredSorted = useMemo(() => {
    let list = rawHosts || [];
    if (favoritesOnly) list = list.filter((h) => h.is_favorite);
    if (filters.host_type !== "all")
      list = list.filter((h) => h.host_type === filters.host_type);
    if (filters.system_tier !== "all")
      list = list.filter((h) => h.system_tier === filters.system_tier);
    if (filters.environment !== "all")
      list = list.filter((h) => h.environment === filters.environment);
    if (filters.os !== "all") list = list.filter((h) => h.os === filters.os);

    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((h) =>
        [
          h.hostname,
          h.ip,
          h.host_type,
          h.location_name,
          h.system_name || "",
          h.system_tier || "",
          h.environment || "",
          h.os,
          h.vendor || "",
          h.model || "",
        ]
          .join(" ")
          .toLowerCase()
          .includes(q)
      );
    }

    const { key, dir } = sort;
    const mul = dir === "asc" ? 1 : -1;
    list = [...list].sort((a, b) => {
      if (!favoritesOnly && a.is_favorite !== b.is_favorite) {
        return a.is_favorite ? -1 : 1;
      }
      const av = String(a[key] ?? "").toLowerCase();
      const bv = String(b[key] ?? "").toLowerCase();
      if (av < bv) return -1 * mul;
      if (av > bv) return 1 * mul;
      return 0;
    });

    return list;
  }, [rawHosts, favoritesOnly, filters, search, sort]);

  const onSort = (key) => {
    setSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { key, dir: "asc" }
    );
  };

  const onToggleFav = useCallback(
    async (host) => {
      const next = !host.is_favorite;
      mutate(
        (curr) =>
          (curr || []).map((h) =>
            h.id === host.id ? { ...h, is_favorite: next } : h
          ),
        { revalidate: false }
      );
      try {
        await toggleFavorite(host.id, next);
        toast.success(next ? "Added to favorites" : "Removed from favorites", {
          description: host.hostname,
        });
      } catch (e) {
        toast.error("Could not update favorite");
        mutate();
      }
    },
    [mutate]
  );

  const resetFilters = () => {
    setFilters({
      host_type: "all",
      system_tier: "all",
      environment: "all",
      os: "all",
    });
    setSearch("");
  };

  const favCount = (rawHosts || []).filter((h) => h.is_favorite).length;

  return (
    <TooltipProvider delayDuration={120}>
      <div className="space-y-4">
        {/* Control bar */}
        <div className="bg-white border border-[#E5E7EB] rounded-sm p-4">
          <div className="flex flex-col lg:flex-row lg:items-center gap-3">
            <div className="relative flex-1 min-w-[240px]">
              <MagnifyingGlass
                size={16}
                weight="bold"
                className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]"
              />
              <Input
                data-testid={INV.search}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search hostname, IP, system, location, OS…"
                className="pl-9 pr-9 rounded-sm border-[#D1D5DB] font-mono-tech text-sm focus-visible:ring-1 focus-visible:ring-[#0052CC]"
              />
              {search && (
                <button
                  data-testid={INV.clearSearch}
                  onClick={() => setSearch("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 grid place-items-center text-[#6B7280] hover:text-[#111827]"
                  aria-label="Clear search"
                >
                  <X size={14} weight="bold" />
                </button>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <FilterSelect
                testId={INV.filterType}
                label="Type"
                value={filters.host_type}
                onChange={(v) => setFilters((f) => ({ ...f, host_type: v }))}
                options={meta?.host_types || []}
              />
              <FilterSelect
                testId={INV.filterTier}
                label="Tier"
                value={filters.system_tier}
                onChange={(v) => setFilters((f) => ({ ...f, system_tier: v }))}
                options={meta?.tiers || []}
              />
              <FilterSelect
                testId={INV.filterEnv}
                label="Env"
                value={filters.environment}
                onChange={(v) => setFilters((f) => ({ ...f, environment: v }))}
                options={meta?.environments || []}
              />
              <FilterSelect
                testId={INV.filterOs}
                label="OS"
                value={filters.os}
                onChange={(v) => setFilters((f) => ({ ...f, os: v }))}
                options={meta?.os || []}
              />
              <Button
                data-testid={INV.resetFilters}
                onClick={resetFilters}
                variant="ghost"
                className="h-9 rounded-sm text-[#4B5563] font-heading font-bold uppercase tracking-wide text-xs hover:bg-[#F1F3F5]"
              >
                Reset
              </Button>
            </div>

            <div className="flex items-center gap-2 border-l border-[#E5E7EB] pl-3">
              <Star
                size={16}
                weight={favoritesOnly ? "fill" : "regular"}
                className={favoritesOnly ? "text-[#F59E0B]" : "text-[#6B7280]"}
              />
              <Label
                htmlFor="fav-toggle"
                className="text-xs font-heading uppercase tracking-wider text-[#4B5563] cursor-pointer"
              >
                Favorites only
              </Label>
              <Switch
                id="fav-toggle"
                data-testid={INV.showFavoritesToggle}
                checked={favoritesOnly}
                onCheckedChange={setFavoritesOnly}
              />
            </div>
          </div>

          <div className="mt-3 flex items-center justify-between text-[11px] font-mono-tech text-[#6B7280]">
            <div data-testid={INV.rowCount}>
              {isLoading ? (
                "Loading…"
              ) : (
                <>
                  <span className="text-[#111827] font-semibold">
                    {filteredSorted.length}
                  </span>{" "}
                  of {rawHosts?.length || 0} hosts · {favCount} favorited
                </>
              )}
            </div>
            <div>
              Sorted by {sort.key} · {sort.dir.toUpperCase()} · row click opens detail
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white border border-[#E5E7EB] rounded-sm">
          <div className="overflow-auto tech-scroll max-h-[calc(100vh-320px)] sticky-header">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b border-[#E5E7EB] bg-[#F8F9FA]">
                  <th className="w-[44px] px-3 py-2.5 text-left"></th>
                  {COLUMNS.map((c) => (
                    <SortHeader
                      key={c.key}
                      col={c}
                      sort={sort}
                      onSort={onSort}
                    />
                  ))}
                  <th className="px-3 py-2.5 text-right text-[11px] font-heading font-bold uppercase tracking-wider text-[#4B5563] w-[220px]">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredSorted.length === 0 ? (
                  <tr>
                    <td
                      colSpan={COLUMNS.length + 2}
                      data-testid={INV.emptyState}
                      className="py-16 text-center text-[#6B7280]"
                    >
                      <div className="font-heading font-bold text-[#111827] text-lg">
                        No matching hosts
                      </div>
                      <div className="text-xs mt-1">
                        Try clearing filters or the search query.
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredSorted.map((h) => (
                    <HostRow
                      key={h.id}
                      host={h}
                      onToggleFav={onToggleFav}
                      onConnect={() => setConnectHost(h)}
                      onOpen={() => onOpenHost && onOpenHost(h)}
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <ConnectDialog host={connectHost} onClose={() => setConnectHost(null)} />
    </TooltipProvider>
  );
}

function SortHeader({ col, sort, onSort }) {
  const active = sort.key === col.key;
  const Icon = !active ? CaretUpDown : sort.dir === "asc" ? CaretUp : CaretDown;
  return (
    <th
      className={`${col.width} px-3 py-2.5 text-left border-l border-[#E5E7EB] first:border-l-0`}
    >
      <button
        data-testid={INV.sortHeader(col.key)}
        onClick={() => onSort(col.key)}
        className={`flex items-center gap-1.5 text-[11px] font-heading font-bold uppercase tracking-wider ${
          active ? "text-[#0052CC]" : "text-[#4B5563] hover:text-[#111827]"
        } transition-colors`}
      >
        {col.label}
        <Icon size={12} weight="bold" />
      </button>
    </th>
  );
}

function HostRow({ host, onToggleFav, onConnect, onOpen }) {
  const snowUrl = `${SNOW_BASE}?uri=cmdb_ci.do?sysparm_query=name%3D${encodeURIComponent(
    host.hostname
  )}`;
  const ddUrl = `${DATADOG_BASE}?host=${encodeURIComponent(host.hostname)}`;
  const isServer = host.host_type === "Server";

  // Stop propagation so nested interactions don't open the detail tab
  const stop = (e) => e.stopPropagation();

  const onKey = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onOpen();
    }
  };

  return (
    <tr
      data-testid={INV.row(host.id)}
      onClick={onOpen}
      onKeyDown={onKey}
      role="button"
      tabIndex={0}
      className="row-hover border-b border-[#F1F3F5] cursor-pointer focus:outline-none focus:bg-[#F1F5FF]"
    >
      <td className="px-3 py-1.5 align-middle">
        <button
          data-testid={INV.favoriteBtn(host.id)}
          onClick={(e) => {
            stop(e);
            onToggleFav(host);
          }}
          className="h-7 w-7 grid place-items-center rounded-sm hover:bg-[#FEF3C7] transition-colors"
          title={host.is_favorite ? "Unfavorite" : "Favorite"}
        >
          <Star
            size={16}
            weight={host.is_favorite ? "fill" : "regular"}
            className={host.is_favorite ? "text-[#F59E0B]" : "text-[#9CA3AF]"}
          />
        </button>
      </td>
      <td className="px-3 py-1.5 font-mono-tech text-[13px] text-[#111827] whitespace-nowrap">
        {host.hostname}
      </td>
      <td className="px-3 py-1.5 font-mono-tech text-[13px] text-[#4B5563] whitespace-nowrap tabular-nums">
        {host.ip}
      </td>
      <td className="px-3 py-1.5 text-[13px] text-[#111827] whitespace-nowrap">
        {host.host_type}
      </td>
      <td className="px-3 py-1.5 text-[13px] whitespace-nowrap">
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              data-testid={INV.locationCell(host.id)}
              onClick={stop}
              className="inline-flex items-center gap-1.5 text-[#111827] border-b border-dotted border-[#9CA3AF] cursor-help"
            >
              <MapPin size={12} weight="fill" className="text-[#0052CC]" />
              {host.location_name}
            </span>
          </TooltipTrigger>
          <TooltipContent
            side="top"
            className="bg-[#111827] text-white rounded-sm border-none text-xs font-mono-tech px-3 py-1.5"
          >
            {host.location_address}
          </TooltipContent>
        </Tooltip>
      </td>
      <td className="px-3 py-1.5 text-[13px] text-[#111827] whitespace-nowrap">
        {isServer ? host.system_name : <span className="text-[#9CA3AF]">{DASH}</span>}
      </td>
      <td className="px-3 py-1.5">
        {isServer && host.system_tier ? (
          <Badge
            className={`rounded-sm border ${tierBadge(host.system_tier)} font-heading font-bold uppercase text-[10px] tracking-wider px-1.5 py-0`}
          >
            {host.system_tier}
          </Badge>
        ) : (
          <span className="text-[#9CA3AF] text-[13px]">{DASH}</span>
        )}
      </td>
      <td className="px-3 py-1.5">
        {isServer && host.environment ? (
          <Badge
            className={`rounded-sm border ${envBadge(host.environment)} font-heading font-bold uppercase text-[10px] tracking-wider px-1.5 py-0`}
          >
            {host.environment}
          </Badge>
        ) : (
          <span className="text-[#9CA3AF] text-[13px]">{DASH}</span>
        )}
      </td>
      <td className="px-3 py-1.5 text-[13px] text-[#111827] whitespace-nowrap font-mono-tech">
        {host.os}
      </td>
      <td className="px-3 py-1.5">
        <div className="flex items-center justify-end gap-1" onClick={stop}>
          <button
            data-testid={INV.connectBtn(host.id)}
            onClick={(e) => {
              stop(e);
              onConnect();
            }}
            className="h-7 px-2 inline-flex items-center gap-1 rounded-sm bg-[#111827] text-white text-[11px] font-heading font-bold uppercase tracking-wide hover:bg-[#0052CC] transition-colors"
            title="Open remote session"
          >
            <Terminal size={12} weight="bold" />
            Connect
          </button>
          <a
            data-testid={INV.serviceNowBtn(host.id)}
            href={snowUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="h-7 px-2 inline-flex items-center gap-1 rounded-sm border border-[#059669] text-[#059669] text-[11px] font-heading font-bold uppercase tracking-wide hover:bg-[#059669] hover:text-white transition-colors"
            title="Open in ServiceNow"
          >
            <ArrowSquareOut size={12} weight="bold" />
            SNow
          </a>
          <a
            data-testid={INV.datadogBtn(host.id)}
            href={ddUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="h-7 px-2 inline-flex items-center gap-1 rounded-sm border border-[#632CA6] text-[#632CA6] text-[11px] font-heading font-bold uppercase tracking-wide hover:bg-[#632CA6] hover:text-white transition-colors"
            title="Open in Datadog"
          >
            <ChartLine size={12} weight="bold" />
            Datadog
          </a>
        </div>
      </td>
    </tr>
  );
}

function FilterSelect({ testId, label, value, onChange, options }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger
        data-testid={testId}
        className="h-9 w-[128px] rounded-sm border-[#D1D5DB] font-heading uppercase tracking-wider text-[11px] focus:ring-1 focus:ring-[#0052CC]"
      >
        <span className="text-[#6B7280] mr-1">{label}:</span>
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="rounded-sm">
        <SelectItem value="all">All</SelectItem>
        {options.map((o) => (
          <SelectItem key={o} value={o}>
            {o}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function ConnectDialog({ host, onClose }) {
  const open = !!host;
  const isWindows = host?.os === "Windows";
  const proto = isWindows ? "rdp" : "ssh";
  const cmd = isWindows
    ? `mstsc /v:${host?.hostname}`
    : `ssh admin@${host?.hostname}`;
  const uri = `${proto}://${host?.hostname}`;

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`, { description: text });
    } catch (e) {
      toast.error("Clipboard blocked", { description: text });
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid={INV.connectDialog}
        className="rounded-sm border-[#E5E7EB] max-w-lg"
      >
        <DialogHeader>
          <DialogTitle className="font-heading font-black tracking-tight text-xl">
            Initiate remote session
          </DialogTitle>
          <DialogDescription className="text-[#4B5563]">
            Copy the command below and paste it into your terminal, or launch
            the protocol handler.
          </DialogDescription>
        </DialogHeader>
        {host && (
          <div className="space-y-4 mt-2">
            <div className="grid grid-cols-3 gap-3 text-xs font-mono-tech">
              <Field label="Hostname" value={host.hostname} mono />
              <Field label="IP" value={host.ip} mono />
              <Field label="OS" value={host.os} />
              <Field label="Type" value={host.host_type} />
              <Field label="Environment" value={host.environment || "—"} />
              <Field label="Tier" value={host.system_tier || "—"} />
            </div>

            <div className="border border-[#E5E7EB] rounded-sm">
              <div className="px-3 py-2 border-b border-[#E5E7EB] flex items-center justify-between bg-[#F8F9FA]">
                <span className="text-[10px] font-heading font-bold uppercase tracking-widest text-[#4B5563]">
                  {isWindows ? "RDP command" : "SSH command"}
                </span>
                <button
                  data-testid={INV.connectCopyBtn}
                  onClick={() => copy(cmd, "Command")}
                  className="text-[10px] font-heading font-bold uppercase tracking-wider text-[#0052CC] hover:text-[#0043A6] flex items-center gap-1"
                >
                  <Copy size={12} weight="bold" /> Copy
                </button>
              </div>
              <div className="px-3 py-3 font-mono-tech text-sm text-[#111827] bg-white">
                <span className="text-[#9CA3AF] select-none">$ </span>
                {cmd}
              </div>
            </div>

            <div className="flex gap-2">
              <a
                href={uri}
                className="flex-1 h-9 grid place-items-center rounded-sm bg-[#0052CC] hover:bg-[#0043A6] text-white text-xs font-heading font-bold uppercase tracking-wide transition-colors"
              >
                <span className="inline-flex items-center gap-1.5">
                  <Terminal size={14} weight="bold" />
                  Launch {proto.toUpperCase()}
                </span>
              </a>
              <button
                onClick={() => copy(uri, "URI")}
                className="h-9 px-4 rounded-sm border border-[#111827] text-[#111827] text-xs font-heading font-bold uppercase tracking-wide hover:bg-[#111827] hover:text-white transition-colors"
              >
                Copy URI
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value, mono }) {
  return (
    <div>
      <div className="text-[9px] font-heading font-bold uppercase tracking-widest text-[#6B7280]">
        {label}
      </div>
      <div
        className={`mt-0.5 text-[#111827] ${
          mono ? "font-mono-tech" : "font-medium"
        } text-[13px]`}
      >
        {value}
      </div>
    </div>
  );
}
